/**
 * Esprima Studios — Engraving Options Module
 * -------------------------------------------
 * Renders a "Request custom engraving" form scoped to whichever variant is
 * currently selected on a product page, using the rules in
 * engraving-config.js (window.EsprimaEngravingConfig, keyed by variant SKU).
 *
 * On Add to Cart, the buyer's entries are attached to the product form as
 * standard Shopify line-item properties (properties[...]) so they land on
 * the order exactly where SOP_Shopify_to_Stuller_Engraving_Fulfillment.md
 * expects to find them (order detail page, packing slip, notification
 * emails) — no app or integration required.
 *
 * Depends on: engraving-config.js (must load first), engraving-options.css
 */
(function () {
  "use strict";

  var ROOT_ID = "engraving-options";
  var UNSUPPORTED_CHAR_RE = /[À-ɏḀ-ỿ&'’´`’\u{1F300}-\u{1FAFF}☀-➿]/u;

  function init() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;

    var config = window.EsprimaEngravingConfig || {};
    var variantMapEl = document.querySelector("[data-engraving-variant-map]");
    var variantMap = {};
    if (variantMapEl) {
      try {
        var parsed = JSON.parse(variantMapEl.textContent);
        (parsed.variants || []).forEach(function (v) {
          variantMap[String(v.id)] = v.sku;
        });
      } catch (e) {
        console.warn("[engraving-options] could not parse variant map", e);
      }
    }

    var form = root.closest("form") || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Some themes assemble the add-to-cart form from a block system where our
    // module's container isn't a DOM descendant of the real <form> (e.g.
    // Shopify's newer block-based product templates). The HTML `form`
    // attribute associates a control with a form by id regardless of DOM
    // placement, and FormData(form) picks those up automatically — so every
    // dynamically created field below gets tagged with this once we know the
    // form's id. Harmless no-op on themes where our container IS nested
    // inside the form already.
    //
    // IMPORTANT: use getAttribute("id") here, not the `.id` property. Shopify's
    // cart form contains `<input name="id" value="{variant.id}">` (the variant
    // ID field), and per the HTML spec a form-associated element named "id"
    // shadows the form's own `.id` IDL property — so `form.id` resolves to that
    // input element, not the form's id string. `getAttribute("id")` reads the
    // actual id="" attribute and isn't affected by this shadowing.
    var formId = form.getAttribute("id");
    if (!formId) {
      formId = "esprima-options-form-" + Math.random().toString(36).slice(2);
      form.setAttribute("id", formId);
    }
    function bindToForm(el) {
      el.setAttribute("form", formId);
      return el;
    }

    var variantInput = form.querySelector('[name="id"]');
    var state = { spec: null, sku: null, locations: {} };

    function currentVariantId() {
      if (!variantInput) return null;
      return variantInput.value || variantInput.getAttribute("value");
    }

    function resolveSpec() {
      var id = currentVariantId();
      var sku = id != null ? variantMap[String(id)] : null;
      state.sku = sku || null;
      state.spec = sku ? config[sku] : null;
      return state.spec;
    }

    function render() {
      var spec = resolveSpec();
      root.innerHTML = "";

      if (!spec) {
        root.hidden = true;
        return;
      }
      root.hidden = false;
      root.appendChild(buildModule(spec));
      wireEnctype();
    }

    function wireEnctype() {
      // Needed for any location using a Handwritten Message file upload.
      form.setAttribute("enctype", "multipart/form-data");
      form.setAttribute("method", "post");
    }

    function buildModule(spec) {
      var wrap = document.createElement("div");
      wrap.className = "engraving-module";

      var toggleId = "engraving-toggle";
      var toggleWrap = document.createElement("label");
      toggleWrap.className = "engraving-toggle";
      toggleWrap.innerHTML =
        '<input type="checkbox" id="' + toggleId + '"> ' +
        "<span>Add custom engraving to this piece</span>";
      wrap.appendChild(toggleWrap);

      var body = document.createElement("div");
      body.className = "engraving-body";
      body.hidden = true;
      wrap.appendChild(body);

      var intro = document.createElement("p");
      intro.className = "engraving-intro";
      intro.textContent =
        "Precision laser engraving by Stuller. Locations and character limits " +
        "are specific to this piece — " +
        spec.locations.length +
        (spec.locations.length === 1 ? " location available." : " locations available.");
      body.appendChild(intro);

      var locationsEl = document.createElement("div");
      locationsEl.className = "engraving-locations";
      body.appendChild(locationsEl);

      spec.locations.forEach(function (loc) {
        locationsEl.appendChild(buildLocationField(spec, loc));
      });

      var summary = document.createElement("div");
      summary.className = "engraving-summary";
      summary.setAttribute("aria-live", "polite");
      body.appendChild(summary);

      var errorEl = document.createElement("div");
      errorEl.className = "engraving-error";
      errorEl.hidden = true;
      errorEl.setAttribute("role", "alert");
      body.appendChild(errorEl);

      var noteEl = document.createElement("p");
      noteEl.className = "engraving-fine-print";
      noteEl.textContent =
        "We'll confirm the exact engraving text with you before it's sent to " +
        "production. Engraved pieces are final sale. Expect added lead time " +
        "beyond standard shipping.";
      body.appendChild(noteEl);

      function refreshSummary() {
        var parts = [];
        spec.locations.forEach(function (loc) {
          var s = state.locations[loc.id];
          if (s && s.enabled && s.text) {
            var typeLabel = s.type === "handwritten" ? "Handwritten Message" : "Text";
            parts.push(
              loc.label + ': "' + s.text + '" · ' + typeLabel +
              " · " + s.font + " · Color: " + s.color
            );
          } else if (s && s.enabled && s.type === "handwritten" && s.hasFile) {
            parts.push(loc.label + ": Handwritten Message (image attached) · " + s.font + " · Color: " + s.color);
          }
        });
        summary.textContent = parts.length
          ? "Your engraving will read — " + parts.join("  |  ")
          : "";
      }

      function refreshErrors() {
        var toggleOn = toggleWrap.querySelector("input").checked;
        var errors = [];
        if (toggleOn) {
          var anyContent = false;
          spec.locations.forEach(function (loc) {
            var s = state.locations[loc.id];
            if (!s || !s.enabled) return;
            if (s.type === "text") {
              if (s.text && s.text.length > loc.maxLength) {
                errors.push(loc.label + " exceeds the " + loc.maxLength + "-character limit for this piece.");
              }
              if (s.text) anyContent = true;
            } else if (s.type === "handwritten") {
              if (!s.hasFile) {
                errors.push(loc.label + " is set to Handwritten Message but no image has been uploaded.");
              } else {
                anyContent = true;
              }
            }
          });
          if (!anyContent) {
            errors.push("Enter engraving text or upload a handwritten message for at least one location, or turn off custom engraving.");
          }
        }
        errorEl.hidden = errors.length === 0;
        errorEl.textContent = errors.join(" ");
        return errors.length === 0;
      }

      body.addEventListener("engraving:update", function () {
        refreshSummary();
        refreshErrors();
      });

      toggleWrap.querySelector("input").addEventListener("change", function (e) {
        body.hidden = !e.target.checked;
        setLocationsEnabled(spec, e.target.checked);
        body.dispatchEvent(new CustomEvent("engraving:update"));
      });

      // Block add-to-cart until valid whenever engraving is toggled on.
      form.addEventListener("submit", function (evt) {
        var toggleOn = toggleWrap.querySelector("input").checked;
        if (!toggleOn) return; // no engraving requested — nothing to validate
        var ok = refreshErrors();
        if (!ok) {
          evt.preventDefault();
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      wrap.__refresh = function () {
        body.dispatchEvent(new CustomEvent("engraving:update"));
      };

      return wrap;
    }

    function setLocationsEnabled(spec, enabled) {
      spec.locations.forEach(function (loc) {
        var s = state.locations[loc.id];
        if (!s) return;
        s.enabled = enabled;
        // Native `disabled` on the underlying inputs keeps them out of the
        // form submission entirely when engraving is turned off, without
        // us having to remove/re-add DOM nodes.
        Object.keys(s.inputs).forEach(function (key) {
          var el = s.inputs[key];
          if (!el) return;
          if (key === "typeRadios") {
            el.forEach(function (radio) { radio.disabled = !enabled; });
            return;
          }
          el.disabled = !enabled;
        });
        applyTypeVisibility(loc, s);
      });
    }

    function applyTypeVisibility(loc, s) {
      var isHandwritten = s.type === "handwritten";
      if (s.inputs.text) {
        s.inputs.text.closest(".engraving-field").hidden = isHandwritten;
        s.inputs.text.disabled = !s.enabled || isHandwritten;
      }
      if (s.inputs.file) {
        s.inputs.file.closest(".engraving-field").hidden = !isHandwritten;
        s.inputs.file.disabled = !s.enabled || !isHandwritten;
      }
    }

    function buildLocationField(spec, loc) {
      var fieldset = document.createElement("fieldset");
      fieldset.className = "engraving-location";

      var legend = document.createElement("legend");
      legend.textContent = loc.label + " — up to " + loc.maxLength + " character" + (loc.maxLength === 1 ? "" : "s");
      fieldset.appendChild(legend);

      var s = {
        enabled: false,
        type: spec.engravingTypes[0],
        text: "",
        hasFile: false,
        font: spec.defaultFont,
        color: spec.defaultColor,
        inputs: {}
      };
      state.locations[loc.id] = s;

      var namePrefix = "Engraving — " + loc.label + " — ";

      // Engraving type (Text vs Handwritten Message), only if both offered.
      if (spec.engravingTypes.length > 1) {
        var typeRow = document.createElement("div");
        typeRow.className = "engraving-field engraving-type-row";
        spec.engravingTypes.forEach(function (t, i) {
          var id = "engraving-type-" + loc.id + "-" + t;
          var label = document.createElement("label");
          label.className = "engraving-radio";
          label.innerHTML =
            '<input type="radio" name="engraving-type-' + loc.id + '" id="' + id + '" value="' + t + '"' +
            (i === 0 ? " checked" : "") + " disabled> " +
            "<span>" + (t === "handwritten" ? "Handwritten Message" : "Text") + "</span>";
          typeRow.appendChild(label);
        });
        fieldset.appendChild(typeRow);
        s.inputs.typeRadios = Array.prototype.slice.call(typeRow.querySelectorAll('input[type="radio"]'));
        typeRow.addEventListener("change", function (e) {
          if (e.target.name !== "engraving-type-" + loc.id) return;
          s.type = e.target.value;
          applyTypeVisibility(loc, s);
          fieldset.dispatchEvent(new Event("engraving:local-update", { bubbles: true }));
        });
        // Radio inputs start disabled (rendered with `disabled` above) until
        // the "Add custom engraving" toggle turns this location on.
      }

      // Text field
      var textField = document.createElement("div");
      textField.className = "engraving-field";
      var textId = "engraving-text-" + loc.id;
      var textLabel = document.createElement("label");
      textLabel.setAttribute("for", textId);
      textLabel.textContent = "Engraving text";
      textField.appendChild(textLabel);
      var textInput = document.createElement("input");
      textInput.type = "text";
      textInput.id = textId;
      textInput.name = "properties[" + namePrefix + "Text]";
      textInput.maxLength = loc.maxLength;
      textInput.autocomplete = "off";
      textInput.disabled = true;
      bindToForm(textInput);
      textField.appendChild(textInput);
      var counter = document.createElement("span");
      counter.className = "engraving-counter";
      counter.setAttribute("aria-live", "polite");
      counter.textContent = "0 / " + loc.maxLength;
      textField.appendChild(counter);
      var warn = document.createElement("span");
      warn.className = "engraving-warn";
      warn.hidden = true;
      warn.textContent = "This character set isn't confirmed as supported — we'll verify before submitting your order.";
      textField.appendChild(warn);
      fieldset.appendChild(textField);
      s.inputs.text = textInput;

      textInput.addEventListener("input", function () {
        s.text = textInput.value.trim();
        counter.textContent = textInput.value.length + " / " + loc.maxLength;
        counter.classList.toggle("is-over", textInput.value.length > loc.maxLength);
        warn.hidden = !UNSUPPORTED_CHAR_RE.test(textInput.value);
        fieldset.dispatchEvent(new Event("engraving:local-update", { bubbles: true }));
      });

      // Handwritten message file upload (only relevant if offered)
      if (spec.engravingTypes.indexOf("handwritten") !== -1) {
        var fileField = document.createElement("div");
        fileField.className = "engraving-field";
        fileField.hidden = spec.engravingTypes[0] !== "handwritten";
        var fileId = "engraving-file-" + loc.id;
        var fileLabel = document.createElement("label");
        fileLabel.setAttribute("for", fileId);
        fileLabel.textContent = "Upload handwritten message (image)";
        fileField.appendChild(fileLabel);
        var fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.id = fileId;
        fileInput.name = "properties[" + namePrefix + "Handwritten Image]";
        fileInput.accept = "image/*";
        fileInput.disabled = true;
        bindToForm(fileInput);
        fileField.appendChild(fileInput);
        fieldset.appendChild(fileField);
        s.inputs.file = fileInput;

        fileInput.addEventListener("change", function () {
          s.hasFile = !!(fileInput.files && fileInput.files.length);
          fieldset.dispatchEvent(new Event("engraving:local-update", { bubbles: true }));
        });
      }

      // Font
      var fontField = document.createElement("div");
      fontField.className = "engraving-field";
      if (spec.fonts.length > 1) {
        var fontId = "engraving-font-" + loc.id;
        var fontLabel = document.createElement("label");
        fontLabel.setAttribute("for", fontId);
        fontLabel.textContent = "Font";
        fontField.appendChild(fontLabel);
        var fontSelect = document.createElement("select");
        fontSelect.id = fontId;
        fontSelect.name = "properties[" + namePrefix + "Font]";
        fontSelect.disabled = true;
        bindToForm(fontSelect);
        spec.fonts.forEach(function (f) {
          var opt = document.createElement("option");
          opt.value = f;
          opt.textContent = f;
          if (f === spec.defaultFont) opt.selected = true;
          fontSelect.appendChild(opt);
        });
        fontField.appendChild(fontSelect);
        s.inputs.font = fontSelect;
        fontSelect.addEventListener("change", function () {
          s.font = fontSelect.value;
        });
      } else {
        var fontStatic = document.createElement("p");
        fontStatic.className = "engraving-static-value";
        fontStatic.textContent = "Font: " + spec.defaultFont;
        fontField.appendChild(fontStatic);
        var fontHidden = document.createElement("input");
        fontHidden.type = "hidden";
        fontHidden.name = "properties[" + namePrefix + "Font]";
        fontHidden.value = spec.defaultFont;
        fontHidden.disabled = true;
        bindToForm(fontHidden);
        fontField.appendChild(fontHidden);
        s.inputs.fontHidden = fontHidden;
      }
      fieldset.appendChild(fontField);

      // Color
      var colorField = document.createElement("div");
      colorField.className = "engraving-field";
      var colorId = "engraving-color-" + loc.id;
      var colorLabel = document.createElement("label");
      colorLabel.setAttribute("for", colorId);
      colorLabel.textContent = "Color infill";
      colorField.appendChild(colorLabel);
      var colorSelect = document.createElement("select");
      colorSelect.id = colorId;
      colorSelect.name = "properties[" + namePrefix + "Color]";
      colorSelect.disabled = true;
      bindToForm(colorSelect);
      spec.colors.forEach(function (c) {
        var opt = document.createElement("option");
        opt.value = c;
        opt.textContent = c;
        if (c === spec.defaultColor) opt.selected = true;
        colorSelect.appendChild(opt);
      });
      colorField.appendChild(colorSelect);
      fieldset.appendChild(colorField);
      s.inputs.color = colorSelect;
      colorSelect.addEventListener("change", function () {
        s.color = colorSelect.value;
      });

      fieldset.addEventListener("engraving:local-update", function () {
        fieldset.dispatchEvent(new CustomEvent("engraving:update", { bubbles: true }));
      });

      return fieldset;
    }

    // Re-render whenever the selected variant changes.
    if (variantInput) {
      variantInput.addEventListener("change", render);
    }
    // Common theme custom events, in case the variant picker updates a
    // hidden input without firing a native `change` (harmless if unused).
    form.addEventListener("variant:change", render);
    form.addEventListener("on:variant:change", render);

    render();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
